"use client";

import { c as _c } from "react/compiler-runtime";
import "@/features/home-v2/overlays.css";
import { useEffect, useMemo, useState } from "react";
import { Download, Share2, X } from "lucide-react";
import { toast } from "sonner";
import type { Locale } from "@/i18n-config";
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}
type PWAControllerProps = {
  lang: Locale;
};
const DISMISS_KEY = "nftbaz-pwa-install-dismissed";
export default function PWAController(t0) {
  const $ = _c(34);
  const {
    lang
  } = t0;
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(true);
  const [isIosBrowser, setIsIosBrowser] = useState(false);
  let t1;
  if ($[0] !== lang) {
    t1 = lang === "fa" ? {
      title: "\u0646\u0635\u0628 \u0627\u067E NFTBaz",
      description: "\u0628\u0631\u0627\u06CC \u062A\u062C\u0631\u0628\u0647 \u0633\u0631\u06CC\u0639\u200C\u062A\u0631 \u0648 \u0627\u062C\u0631\u0627\u06CC \u062A\u0645\u0627\u0645\u200C\u0635\u0641\u062D\u0647\u060C \u0627\u067E \u0631\u0627 \u0631\u0648\u06CC \u062F\u0633\u062A\u06AF\u0627\u0647\u062A \u0646\u0635\u0628 \u06A9\u0646.",
      iosDescription: "\u062F\u0631 Safari \u0627\u0632 \u062F\u06A9\u0645\u0647 \u0627\u0634\u062A\u0631\u0627\u06A9\u200C\u06AF\u0630\u0627\u0631\u06CC \u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u06A9\u0646 \u0648 \u0633\u067E\u0633 Add to Home Screen \u0631\u0627 \u0628\u0632\u0646.",
      short: "\u0627\u062C\u0631\u0627\u06CC \u0633\u0631\u06CC\u0639\u200C\u062A\u0631 \u0648 \u062A\u0645\u0627\u0645\u200C\u0635\u0641\u062D\u0647",
      iosShort: "\u0627\u0634\u062A\u0631\u0627\u06A9\u200C\u06AF\u0630\u0627\u0631\u06CC \u2190 Add to Home Screen",
      close: "\u0628\u0633\u062A\u0646",
      install: "\u0646\u0635\u0628 \u0627\u067E",
      later: "\u0628\u0639\u062F\u0627",
      share: "\u0627\u0634\u062A\u0631\u0627\u06A9\u200C\u06AF\u0630\u0627\u0631\u06CC",
      updateTitle: "\u0646\u0633\u062E\u0647 \u062C\u062F\u06CC\u062F \u0627\u067E \u0622\u0645\u0627\u062F\u0647 \u0627\u0633\u062A",
      updateAction: "\u0628\u0647\u200C\u0631\u0648\u0632\u0631\u0633\u0627\u0646\u06CC"
    } : {
      title: "Install NFTBaz",
      description: "Install the app for faster startup, better caching and a full-screen experience.",
      iosDescription: "In Safari, tap Share and then choose Add to Home Screen.",
      short: "Faster, full-screen experience",
      iosShort: "Share \u2192 Add to Home Screen",
      close: "Close",
      install: "Install App",
      later: "Later",
      share: "Share",
      updateTitle: "A new app version is ready",
      updateAction: "Update"
    };
    $[0] = lang;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copy = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const standalone = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as Navigator & {
        standalone?: boolean;
      }).standalone === true;
      setIsStandalone(standalone);
      const ua = window.navigator.userAgent.toLowerCase();
      const isIos = /iphone|ipad|ipod/.test(ua);
      const isSafari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
      setIsIosBrowser(isIos && isSafari);
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== copy.updateAction || $[5] !== copy.updateTitle) {
    t4 = () => {
      const shouldEnablePwa = process.env.NODE_ENV === "production" || process.env.NEXT_PUBLIC_ENABLE_PWA_DEV === "true";
      if (typeof window === "undefined") {
        return;
      }
      if (!("serviceWorker" in navigator)) {
        return;
      }
      if (!shouldEnablePwa) {
        navigator.serviceWorker.getRegistrations().then(_temp2).catch(_temp3);
        if ("caches" in window) {
          caches.keys().then(_temp5).catch(_temp6);
        }
        return;
      }
      let hasRefreshed = false;
      const hadController = Boolean(navigator.serviceWorker.controller);
      const promptForUpdate = registration_0 => {
        if (!registration_0.waiting) {
          return;
        }
        toast.info(copy.updateTitle, {
          duration: Infinity,
          action: {
            label: copy.updateAction,
            onClick: () => {
              registration_0.waiting?.postMessage({
                type: "SKIP_WAITING"
              });
            }
          }
        });
      };
      navigator.serviceWorker.register("/sw.js", {
        scope: "/"
      }).then(registration_1 => {
        promptForUpdate(registration_1);
        registration_1.update().catch(_temp7);
        registration_1.addEventListener("updatefound", () => {
          const newWorker = registration_1.installing;
          if (!newWorker) {
            return;
          }
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              promptForUpdate(registration_1);
            }
          });
        });
      }).catch(_temp8);
      const handleControllerChange = () => {
        if (!hadController || hasRefreshed) {
          return;
        }
        hasRefreshed = true;
        window.location.reload();
      };
      navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);
      return () => {
        navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
      };
    };
    t5 = [copy.updateAction, copy.updateTitle];
    $[4] = copy.updateAction;
    $[5] = copy.updateTitle;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== lang) {
    t6 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const handleBeforeInstallPrompt = event => {
        event.preventDefault();
        setDeferredPrompt(event as BeforeInstallPromptEvent);
        setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
      };
      const handleAppInstalled = () => {
        setDeferredPrompt(null);
        setDismissed(true);
        localStorage.setItem(DISMISS_KEY, "1");
        toast.success(lang === "fa" ? "\u0627\u067E \u0628\u0627 \u0645\u0648\u0641\u0642\u06CC\u062A \u0646\u0635\u0628 \u0634\u062F" : "App installed successfully");
      };
      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.addEventListener("appinstalled", handleAppInstalled);
      return () => {
        window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.removeEventListener("appinstalled", handleAppInstalled);
      };
    };
    t7 = [lang];
    $[8] = lang;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setDismissed(true);
      if (typeof window !== "undefined") {
        localStorage.setItem(DISMISS_KEY, "1");
      }
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const hidePrompt = t8;
  const showPrompt = !isStandalone && !dismissed && (Boolean(deferredPrompt) || isIosBrowser);
  let t9;
  if ($[12] !== deferredPrompt) {
    t9 = async () => {
      if (!deferredPrompt) {
        return;
      }
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setDeferredPrompt(null);
        hidePrompt();
      }
    };
    $[12] = deferredPrompt;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const handleInstall = t9;
  if (!showPrompt) {
    return null;
  }
  const iosHint = isIosBrowser && !deferredPrompt;
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <X aria-hidden={true} />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== copy.close) {
    t11 = <button type="button" className="v2-pwa-x v2-focus" aria-label={copy.close} onClick={hidePrompt}>{t10}</button>;
    $[15] = copy.close;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <img className="v2-pwa-icon" src="/icons/pwa/icon-192.png" alt="" width={36} height={36} />;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== copy.title) {
    t13 = <b id="v2-pwa-title">{copy.title}</b>;
    $[18] = copy.title;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = iosHint ? copy.iosDescription : copy.description;
  const t15 = iosHint ? copy.iosShort : copy.short;
  let t16;
  if ($[20] !== t14 || $[21] !== t15) {
    t16 = <p id="v2-pwa-desc" title={t14}>{t15}</p>;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t13 || $[24] !== t16) {
    t17 = <div className="v2-pwa-copy">{t13}{t16}</div>;
    $[23] = t13;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== copy.install || $[27] !== deferredPrompt || $[28] !== handleInstall) {
    t18 = deferredPrompt ? <button type="button" className="v2-pwa-get v2-focus" onClick={handleInstall}><Download aria-hidden={true} />{copy.install}</button> : <span className="v2-pwa-hint" aria-hidden={true}><Share2 /></span>;
    $[26] = copy.install;
    $[27] = deferredPrompt;
    $[28] = handleInstall;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t11 || $[31] !== t17 || $[32] !== t18) {
    t19 = <div className="v2 v2-pop-portal"><aside className="v2-pwa" role="dialog" aria-labelledby="v2-pwa-title" aria-describedby="v2-pwa-desc">{t11}{t12}{t17}{t18}</aside></div>;
    $[30] = t11;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  return t19;
}
function _temp8() {}
function _temp7() {}
function _temp6() {}
function _temp5(keys) {
  for (const key of keys) {
    if (key.startsWith("nftbaz-pwa")) {
      caches.delete(key).catch(_temp4);
    }
  }
}
function _temp4() {}
function _temp3() {}
function _temp2(registrations) {
  for (const registration of registrations) {
    registration.unregister().catch(_temp);
  }
}
function _temp() {}